import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import { ContentCardMobileImage, ContentCardTheme } from '@/ui-components/molecules/ContentCard/ContentCard.type';
import ContentCardGroup from '@/ui-components/molecules/ContentCard/ContentCardGroup';
import { ContentCardGroupCarousel } from '@/ui-components/molecules/ContentCard/ContentCardGroupCarousel';
import ContentCard from '@/ui-components/molecules/ContentCard/ContentCard';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { resolveContentCardMedia, resolveImageSide, resolveMaxWidthTier } from './contentCardUtils';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { INLINES } from '@contentful/rich-text-types';
import { ReactNode } from 'react';

interface ContentCardProps {
    asset: SiteAssetGroup;
}

export default function ContentCardWrapper({ asset }: ContentCardProps) {
    const { fields, presentation } = asset;
    // The whole card is already a link - ensure inline links are extracted so we don't have anchor tags in anchor tags causing hydration issues
    const stripInlineLinks = {
        [INLINES.HYPERLINK]: (node: unknown, children: ReactNode) => children,
        [INLINES.ENTRY_HYPERLINK]: (node: unknown, children: ReactNode) => children,
        [INLINES.ASSET_HYPERLINK]: (node: unknown, children: ReactNode) => children,
    };
    const isMobileSwipeable = fields?.horizontal_mobile_layout?.toLowerCase() === 'swipeable';
    const cardGroupLayoutTheme = presentation.presentations['ContentCard'].template.toLowerCase() as ContentCardTheme;
    const cardStyles = presentation?.presentations['ContentCard']?.styles;
    const videoLayout = cardStyles?.['Video Layout'];
    const isModalVideo = videoLayout === 'Modal';
    const isTruncated = cardStyles?.['Truncate'] === 'Yes';

    let groupMobileLayout: ContentCardTheme | undefined = undefined;
    if (cardStyles?.['Mobile Layout'] === 'Vertical Stacked') {
        groupMobileLayout = 'vertical';
    } else if (cardStyles?.['Mobile Layout'] === 'Horizontal Stacked') {
        groupMobileLayout = 'horizontal';
    }

    const groupMobileImage: ContentCardMobileImage =
        cardStyles?.['Mobile Image'] === 'Trailing' ? 'trailing' : 'leading';

    const isCarousel = cardStyles?.['Carousel'] === 'Yes' || cardStyles?.['Layout'] === 'Carousel';
    const isFeaturedTemplate = cardGroupLayoutTheme === 'featuredcard';
    const isFeaturedStyle = isFeaturedTemplate ? false : cardStyles?.['Featured'] === 'Yes';
    const carouselUntilDesktopSize =
        isFeaturedStyle && cardGroupLayoutTheme !== 'horizontal' && fields.assets.length > 1;

    const rowType =
        cardGroupLayoutTheme === 'horizontal' &&
        cardStyles?.['Column Type'] === 'One Column' &&
        groupMobileLayout === 'vertical'
            ? 'One per row'
            : cardStyles?.['Row Type'];

    const hasCopyright = fields.assets.some((card) => card.fields.image?.copyrightText);

    const content = fields.assets.map((card, index, array) => {
        const { eyebrow, headline, description, image, video, link, audio, headline_text } = card.fields;

        const videoData = resolveContentCardMedia(video, isModalVideo);

        const theme = isFeaturedStyle && index === 0 ? 'featuredcard' : cardGroupLayoutTheme;
        const maxWidth = resolveMaxWidthTier(theme, array.length);

        const copyright = hasCopyright ? image.copyrightText || ' ' : undefined;
        const contentCardImage =
            !videoData && image?.url ? (
                <ImageWrapper
                    src={image.url}
                    alt={image.altText}
                    optimizedHeight={image.height}
                    optimizedWidth={image.width}
                    style={{ aspectRatio: `${image.width} / ${image.height}` }}
                    maxWidth={maxWidth}
                />
            ) : (
                <></>
            );

        const imageSide = resolveImageSide(cardStyles?.Layout, cardStyles?.['Image Align'], index);

        const mobileLayout =
            theme === 'featuredcard' && !carouselUntilDesktopSize ? 'featuredcard' : groupMobileLayout || theme;
        const tabletLayout = carouselUntilDesktopSize && index === 0 ? 'vertical' : theme;
        let smallVerticalMedia = false;

        if (
            cardGroupLayoutTheme === 'horizontal' &&
            cardStyles?.['Column Type'] === 'One Column' &&
            mobileLayout === 'vertical'
        ) {
            smallVerticalMedia = true;
        }

        const audioProp = audio ? { source: audio.url, name: audio.altText } : undefined;
        const customRenderNodes = link?.fields?.href ? stripInlineLinks : undefined;
        const cardData = {
            eyebrow,
            title: headline_text ? (
                <RenderRTE contentfulDoc={headline_text} removeSpanTag customRenderNodes={customRenderNodes} />
            ) : (
                headline
            ),
            body: <RenderRTE contentfulDoc={description} removeSpanTag customRenderNodes={customRenderNodes} />,
            image: contentCardImage,
            imageSide,
            smallVerticalMedia,
            video: videoData,
            audio: audioProp,
            linkProps: {
                href: link?.fields?.href,
                children: link?.fields?.link_text,
                target: link?.fields?.link_target || '_blank',
            },
            mobileLayout,
            mobileImage: groupMobileImage,
            tabletLayout,
            theme,
            copyright,
            isTruncated,
            titleText: headline_text ? documentToPlainTextString(headline_text) : '',
        };

        return <ContentCard key={index} {...cardData} />;
    });

    if (isCarousel) {
        return (
            <ContentCardGroupCarousel cardCount={content.length} theme={cardGroupLayoutTheme}>
                {content}
            </ContentCardGroupCarousel>
        );
    }

    return (
        <ContentCardGroup
            cardCount={content.length}
            theme={cardGroupLayoutTheme}
            carouselUntilDesktopSize={carouselUntilDesktopSize}
            isMobileSwipeable={isMobileSwipeable}
            rowType={rowType}
        >
            {content}
        </ContentCardGroup>
    );
}
